import { useState, useEffect } from 'react';

/**
 * Hook to manage staggered animations for a list of items
 *
 * @param {Object} options
 * @param {number} options.itemCount Total number of items to animate
 * @param {number} options.staggerDelay Delay between each item animation in ms
 * @param {number} options.initialDelay Initial delay before starting animations in ms
 * @returns {JSX.Element}
 */
export const useStaggeredAnimation = ({
    itemCount,
    staggerDelay = 100,
    initialDelay = 0,
}: {
    itemCount: number;
    staggerDelay?: number;
    initialDelay?: number;
}): boolean[] => {
    const [visibleItems, setVisibleItems] = useState<boolean[]>(Array(itemCount).fill(false));

    useEffect(() => {
        // Skip animation if there is only one item or less
        if (itemCount <= 1) {
            setVisibleItems(Array(itemCount).fill(true));
            return;
        }

        // Reset animation when item count changes
        setVisibleItems(Array(itemCount).fill(false));

        const timeouts: ReturnType<typeof setTimeout>[] = [];

        // Set up staggered animations
        for (let i = 0; i < itemCount; i++) {
            const timeout = setTimeout(
                () => {
                    setVisibleItems(prev => {
                        const newState = [...prev];
                        newState[i] = true;

                        return newState;
                    });
                },
                initialDelay + i * staggerDelay
            );

            timeouts.push(timeout);
        }

        return () => {
            timeouts.forEach(timeout => clearTimeout(timeout));
        };
    }, [itemCount, staggerDelay, initialDelay]);

    return visibleItems;
};

export default useStaggeredAnimation;
